/**
 * Utilidades para trabajar con Firestore
 * 
 * Firestore no acepta campos con valor `undefined`, solo acepta:
 * - Valores válidos (string, number, Date, boolean, etc.)
 * - null para campos vacíos
 * - Campos omitidos completamente
 * 
 * Este módulo proporciona funciones para limpiar datos antes de enviarlos a Firestore
 */

/**
 * Sanitiza un objeto para Firestore convirtiendo undefined a null
 * 
 * @param obj - Objeto a sanitizar
 * @returns Objeto con todos los undefined convertidos a null
 * 
 * @example
 * ```typescript
 * const data = { nombre: 'Test', email: undefined, edad: 25 };
 * const sanitized = sanitizeForFirestore(data);
 * // { nombre: 'Test', email: null, edad: 25 }
 * ```
 */
export const sanitizeForFirestore = (obj: unknown): unknown => {
  // Manejar valores null, undefined y primitivos
  if (obj === null || obj === undefined) return null;
  if (obj instanceof Date) return obj;
  if (typeof obj !== 'object') return obj;
  
  // Manejar arrays
  if (Array.isArray(obj)) {
    return obj.map(sanitizeForFirestore);
  }
  
  // Manejar objetos
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    cleaned[key] = value === undefined ? null : sanitizeForFirestore(value);
  }
  return cleaned;
};

/**
 * Elimina campos undefined en vez de convertirlos a null
 * Útil cuando se prefiere omitir campos vacíos completamente
 * 
 * @param obj - Objeto a limpiar
 * @returns Objeto sin campos undefined
 * 
 * @example
 * ```typescript
 * const data = { nombre: 'Test', email: undefined, edad: 25 };
 * const cleaned = removeUndefinedFields(data);
 * // { nombre: 'Test', edad: 25 }  (email omitido)
 * ```
 */
export const removeUndefinedFields = (obj: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      // Recursivo para objetos anidados
      if (value && typeof value === 'object' && !(value instanceof Date) && !Array.isArray(value)) {
        cleaned[key] = removeUndefinedFields(value as Record<string, unknown>);
      } else {
        cleaned[key] = value;
      }
    }
  }
  
  return cleaned;
};

/**
 * Prepara datos para actualización parcial en Firestore
 * Solo incluye los campos que han cambiado y tienen valores válidos
 * 
 * @param original - Objeto original
 * @param updated - Objeto con cambios
 * @returns Objeto con solo los campos modificados, sanitizados
 */
export const prepareUpdateData = (
  original: Record<string, unknown>,
  updated: Record<string, unknown>
): Record<string, unknown> => {
  const changes: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(updated)) {
    // Solo incluir si cambió y no es undefined
    if (original[key] !== value && value !== undefined) {
      changes[key] = value === null ? null : sanitizeForFirestore(value);
    }
  }
  
  return changes;
};

